import React from 'react';
import { Users, FileText, Building, TrendingUp } from 'lucide-react';
import MetricsGrid from '@components/common/MetricsGrid';
import MetricCard from '@components/common/MetricCard';

interface MetricsData {
  assessments: { total: number; byStatus: Array<{ status: string; count: number }> };
  suppliers: { total: number };
  contracts: { total: number };
}

interface DashboardMetricsProps {
  data: MetricsData;
  isLoading: boolean;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ data, isLoading }) => {
  // Calculate derived metrics
  const activeAssessments = data.assessments.byStatus
    .filter((item) =>
      ['Pending', 'Scheduled', 'Rescheduled', 'Pending Evidence', 'Evidence Past Due', 'Escalated'].includes(item.status)
    )
    .reduce((sum, item) => sum + item.count, 0);

  const completedAssessments = data.assessments.byStatus.find((item) => item.status === 'Final')?.count || 0;

  const completionRate =
    data.assessments.total > 0 ? Math.round((completedAssessments / data.assessments.total) * 100) : 0;

  const getCompletionVariant = (): 'green' | 'orange' | 'red' => {
    if (completionRate >= 70) return 'green';
    if (completionRate >= 40) return 'orange';
    return 'red';
  };

  const metrics = [
    {
      label: 'Total Assessments',
      value: data.assessments.total,
      icon: FileText,
      variant: 'blue' as const,
      subtitle: `${activeAssessments} active`
    },
    {
      label: 'Completion Rate',
      value: `${completionRate}%`,
      icon: TrendingUp,
      variant: getCompletionVariant(),
      subtitle: `${completedAssessments} completed`
    },
    {
      label: 'Suppliers',
      value: data.suppliers.total,
      icon: Building,
      variant: 'purple' as const,
      subtitle: 'Under management'
    },
    {
      label: 'Active Contracts',
      value: data.contracts.total,
      icon: Users,
      variant: 'blue' as const,
      subtitle: 'In assessment scope'
    }
  ];

  return (
    <MetricsGrid>
      {metrics.map((metric) => {
        const IconComponent = metric.icon;
        return (
          <MetricCard
            key={`metric-${metric.label}`}
            icon={<IconComponent size={24} />}
            label={metric.label}
            value={metric.value}
            subtitle={metric.subtitle}
            variant={metric.variant}
            loading={isLoading}
          />
        );
      })}
    </MetricsGrid>
  );
};

export default DashboardMetrics;
